import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Users, Circuitry, Wallet, Buildings, House, Spinner,
  ArrowRight, WifiHigh, WifiSlash, ShieldWarning, Lightning,
  CurrencyCircleDollar, Receipt, FileText, ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

import { SubscriptionBanner } from "../components/customer/SubscriptionBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [consumptionData, setConsumptionData] = useState([]);
  const [pendingTopups, setPendingTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, txnRes, chartRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/customer/stats`),
        axios.get(`${API_URL}/api/transactions`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/consumption/chart?days=14`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/api/customer/pending-topups`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setTransactions(txnRes.data || []);
      setConsumptionData(chartRes.data?.data || []);
      setPendingTopups(pendingRes.data || []);
    } catch { toast.error(t("common.error_loading")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApproveTopup = async (requestId) => {
    setApprovingId(requestId);
    try {
      await axios.post(`${API_URL}/api/customer/pending-topups/${requestId}/approve`);
      toast.success(t("dashboard.approved"));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Onay hatasi"); }
    finally { setApprovingId(null); }
  };

  const handleRejectTopup = async (requestId) => {
    setApprovingId(requestId);
    try {
      await axios.post(`${API_URL}/api/customer/pending-topups/${requestId}/reject`);
      toast.success(t("dashboard.rejected"));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Islem hatasi"); }
    finally { setApprovingId(null); }
  };

  const downloadReceipt = async (txnId) => {
    try {
      const res = await axios.get(`${API_URL}/api/transactions/${txnId}/receipt-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `makbuz_${txnId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Makbuz indirilemedi"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  const isStaff = user?.role === "staff";
  const permissions = user?.permissions || {};

  return (
    <div className="space-y-6" data-testid="customer-dashboard">
      <SubscriptionBanner />

      {/* Welcome */}
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900">{t("dashboard.welcome")}, {user?.name}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("dashboard.summary")}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t("dashboard.total_apartments"), value: stats?.apartments_count || 0, icon: Buildings, bg: "bg-indigo-50", text: "text-indigo-600", link: "/apartments" },
          { label: t("dashboard.total_units"), value: stats?.units_count || 0, icon: House, bg: "bg-emerald-50", text: "text-emerald-600", link: "/apartments" },
          { label: t("dashboard.total_devices"), value: stats?.devices_count || 0, icon: Circuitry, bg: "bg-amber-50", text: "text-amber-600", link: "/devices" },
          { label: t("dashboard.total_tenants"), value: stats?.tenants_count || 0, icon: Users, bg: "bg-purple-50", text: "text-purple-600", link: "/tenants" },
        ].map(({ label, value, icon: Icon, bg, text, link }) => (
          <Link key={label} to={link}>
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center shrink-0`}>
                    <Icon size={20} weight="duotone" className={text} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-500 truncate">{label}</p>
                    <p className="text-xl font-bold text-slate-900">{value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Financial Stats - Hidden for staff */}
      {!isStaff && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t("dashboard.total_debt"), value: `${(stats?.total_outstanding_debt || 0).toFixed(0)} TL`, icon: Wallet, bg: "bg-rose-50", text: "text-rose-600" },
          { label: t("dashboard.total_consumption"), value: `${stats?.total_energy_kwh || 0} kWh`, icon: Lightning, bg: "bg-sky-50", text: "text-sky-600" },
          { label: t("dashboard.monthly_topup"), value: `${(stats?.monthly_topup || 0).toFixed(0)} TL`, icon: CurrencyCircleDollar, bg: "bg-teal-50", text: "text-teal-600" },
          { label: t("dashboard.monthly_cost"), value: `${(stats?.monthly_consumption || 0).toFixed(0)} TL`, icon: ChartLineUp, bg: "bg-orange-50", text: "text-orange-600" },
        ].map(({ label, value, icon: Icon, bg, text }) => (
          <Card key={label} className="border-slate-200 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center shrink-0`}>
                  <Icon size={20} weight="duotone" className={text} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-500 truncate">{label}</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* Pending Topup Requests - Staff only sees if has transfer_approve */}
      {pendingTopups.length > 0 && (!isStaff || permissions.transfer_approve) && (
        <Card className="border-amber-300 bg-amber-50/50 shadow-sm" data-testid="pending-topups-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <CurrencyCircleDollar size={18} className="text-amber-600" />
              {t("dashboard.pending_approvals")} ({pendingTopups.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingTopups.map(req => (
                <div key={req.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-white rounded-xl p-4 border border-amber-200 shadow-sm gap-3" data-testid={`pending-topup-${req.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-slate-800">{req.tenant_name}</span>
                      <Badge className={`text-xs ${req.payment_method === "cash" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                        {req.payment_method === "cash" ? "Nakit" : "Havale"}
                      </Badge>
                      <Badge className="bg-amber-100 text-amber-700 text-xs">{t("dashboard.waiting_approval")}</Badge>
                    </div>
                    <p className="text-sm text-slate-500">{req.apartment_name} - {req.unit_name}</p>
                    {req.reference_number && (
                      <p className="text-xs text-blue-600 mt-0.5">Ref: {req.reference_number}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">{new Date(req.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                    <span className="text-xl font-bold text-slate-900">{req.amount?.toFixed(2)} TL</span>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3 flex-1 sm:flex-none"
                        disabled={approvingId === req.id} onClick={() => handleApproveTopup(req.id)}
                        data-testid={`approve-topup-${req.id}`}>
                        {approvingId === req.id ? <Spinner size={14} className="animate-spin" /> : t("common.confirm")}
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 px-3 border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                        disabled={approvingId === req.id} onClick={() => handleRejectTopup(req.id)}
                        data-testid={`reject-topup-${req.id}`}>
                        {t("common.reject")}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Device Status + Recent Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Status */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Circuitry size={18} className="text-indigo-600" /> {t("dashboard.device_status")}
            </CardTitle>
            <Link to="/devices" className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
              {t("common.detail")} <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl" data-testid="online-devices-card">
                <div className="flex items-center gap-2">
                  <WifiHigh size={18} className="text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Online</span>
                </div>
                <span className="text-xl font-bold text-emerald-700">{stats?.online_devices || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid="offline-devices-card">
                <div className="flex items-center gap-2">
                  <WifiSlash size={18} className="text-slate-500" />
                  <span className="text-sm font-medium text-slate-600">Offline</span>
                </div>
                <span className="text-xl font-bold text-slate-700">{stats?.offline_devices || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ShieldWarning size={18} className="text-red-500" /> {t("dashboard.recent_alerts")}
            </CardTitle>
            <Link to="/alerts" className="text-xs text-indigo-600 hover:text-indigo-700">{t("common.view_all")}</Link>
          </CardHeader>
          <CardContent>
            {(stats?.recent_alerts?.length || 0) === 0 ? (
              <div className="text-center py-6">
                <ShieldWarning size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">{t("dashboard.no_alerts")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.recent_alerts.slice(0, 3).map((alert, i) => (
                  <div key={alert.id || i} className={`p-3 rounded-xl border text-sm ${
                    alert.severity === "critical" ? "bg-red-50 border-red-200" :
                    alert.severity === "warning" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"
                  }`} data-testid={`recent-alert-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate flex-1">{alert.device_name}</span>
                      <Badge className={`text-[10px] ml-2 ${
                        alert.severity === "critical" ? "bg-red-100 text-red-700" :
                        alert.severity === "warning" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                      }`}>{alert.severity === "critical" ? t("common.critical") : alert.severity === "warning" ? t("common.warning") : t("common.info")}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 truncate">{alert.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Consumption / Topup Chart - Hidden for staff */}
      {!isStaff && (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <ChartLineUp size={18} className="text-emerald-600" /> {t("dashboard.chart_title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {consumptionData.length > 0 ? (
            <div className="h-52 sm:h-64" data-testid="consumption-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={consumptionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px" }}
                    formatter={(value, name) => [`${Number(value).toFixed(2)} TL`, name === "topup" ? t("dashboard.topup_label") : t("dashboard.consumption_label")]}
                  />
                  <Bar dataKey="topup" name={t("dashboard.topup_label")} fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cost" name={t("dashboard.consumption_label")} fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center">
              <div className="text-center">
                <ChartLineUp size={36} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">{t("common.no_data_yet")}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Recent Transactions - Hidden for staff without reports_view */}
      {(!isStaff || permissions.reports_view) && (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Receipt size={18} className="text-indigo-600" /> {t("dashboard.recent_transactions")}
          </CardTitle>
          <Link to="/reports" className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
            {t("common.view_all")} <ArrowRight size={14} />
          </Link>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-6">
              <Receipt size={36} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">{t("dashboard.no_transactions")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-medium text-slate-500">{t("common.date")}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-500">{t("sidebar.tenants")}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-500 hidden sm:table-cell">{t("common.description")}</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-500">{t("common.amount")}</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-500">{t("common.receipt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.slice(0, 5).map((tx) => (
                    <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`txn-row-${tx.id}`}>
                      <td className="py-3 px-2 text-slate-600 text-xs sm:text-sm">
                        {new Date(tx.created_at).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}
                      </td>
                      <td className="py-3 px-2 text-slate-900 font-medium text-xs sm:text-sm">{tx.tenant_name || "-"}</td>
                      <td className="py-3 px-2 text-slate-600 hidden sm:table-cell">{tx.description}</td>
                      <td className="py-3 px-2 text-right">
                        <span className={tx.type === "topup" ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                          {tx.type === "topup" ? "+" : "-"}{Math.abs(tx.amount).toFixed(2)} TL
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <button onClick={() => downloadReceipt(tx.id)}
                          className="text-indigo-600 hover:text-indigo-700 transition-colors" title="Makbuzu Indir"
                          data-testid={`download-receipt-${tx.id}`}>
                          <FileText size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default CustomerDashboard;
